<template>
  <div class="container-fluid p-3 p-md-4">
    <div class="d-flex justify-content-between align-items-start gap-2 mb-4 flex-wrap">
      <div>
        <h2 class="mb-0">
          <i :class="[NAV_ITEMS.OrderHistory.icon, NAV_ITEMS.OrderHistory.colorClass, 'me-2']"></i>Orders
        </h2>
        <small class="text-muted">Every broker order the system submitted, how it ended and why — a bracket's entry with its two legs under it</small>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap justify-content-end">
        <button class="btn btn-sm" :class="neverFilledActive ? 'btn-warning' : 'btn-outline-warning'"
                title="Bracket entries that ended at the 15:00 cut-off or expired: the price never came back to the level"
                @click="toggleNeverFilled">
          <i class="fas fa-hourglass-end me-1"></i> Entries that never filled
        </button>
      </div>
    </div>

    <div class="card shadow-sm mb-3">
      <form class="card-body d-flex flex-wrap gap-3 align-items-end" @submit.prevent="apply">
        <div>
          <label class="form-label small text-muted mb-1" for="orders-session">Session</label>
          <input id="orders-session" v-model="filters.session_date" type="date" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="form-label small text-muted mb-1" for="orders-ticker">Ticker</label>
          <input id="orders-ticker" v-model="filters.ticker" type="search" class="form-control form-control-sm"
                 placeholder="e.g. AAPL" maxlength="10" />
        </div>
        <div>
          <label class="form-label small text-muted mb-1" for="orders-role">Role</label>
          <select id="orders-role" v-model="filters.bracket_role" class="form-select form-select-sm">
            <option value="">All</option>
            <option value="entry">entry</option>
            <option value="stop_leg">stop leg</option>
            <option value="target_leg">target leg</option>
            <option value="none">plain order</option>
          </select>
        </div>
        <div>
          <label class="form-label small text-muted mb-1" for="orders-state">State</label>
          <select id="orders-state" v-model="filters.state" class="form-select form-select-sm">
            <option value="">All</option>
            <option v-for="s in STATES" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>
        <div>
          <label class="form-label small text-muted mb-1" for="orders-end">End reason</label>
          <select id="orders-end" v-model="filters.end_reason" class="form-select form-select-sm">
            <option value="">All</option>
            <option v-if="isNeverFilledReasons" :value="NEVER_FILLED.end_reason">cut-off or expired</option>
            <option v-for="(meta, reason) in END_REASONS" :key="reason" :value="reason">{{ meta.label }}</option>
          </select>
        </div>
        <button class="btn btn-sm btn-primary" type="submit">Apply</button>
        <button class="btn btn-sm btn-link" type="button" @click="clear">Clear</button>
        <div class="ms-auto small text-muted" v-if="total">
          {{ total }} order{{ total === 1 ? '' : 's' }}<template v-if="total > rows.length"> · showing {{ offset + 1 }}–{{ offset + orders.length }}</template>
        </div>
      </form>
    </div>

    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
    </div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-else-if="!rows.length" class="alert alert-info">
      <i class="fas fa-info-circle me-2"></i>No orders match. Orders are recorded from the first submission after this page was deployed.
    </div>

    <div v-else class="card shadow-sm">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0 align-middle small">
            <thead class="table-light">
              <tr>
                <th>Session</th>
                <th>Time (ET)</th>
                <th>Ticker</th>
                <th>Setup</th>
                <th>Role</th>
                <th>Type</th>
                <th class="text-end">Qty</th>
                <th class="text-end">Limit / Stop</th>
                <th class="text-end">Filled</th>
                <th class="text-end">Avg fill</th>
                <th>State</th>
                <th>End reason</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.key" :class="{ 'leg-row': row.depth, 'entry-row': isEntry(row) }">
                <td class="text-nowrap">{{ row.depth ? '' : row.order.session_date || '—' }}</td>
                <td class="text-nowrap" :title="row.order.submitted_at_utc">{{ etTime(row.order.submitted_at_utc) }}</td>
                <td class="text-nowrap">
                  <span v-if="row.depth" class="text-muted me-1">└</span>
                  <a v-if="row.order.ticker" :href="postMortemHref(row.order)" target="_blank" rel="noopener noreferrer"
                     class="text-primary text-decoration-none fw-bold" :title="`Post-mortem for ${row.order.ticker} on ${row.order.session_date}`">
                    {{ row.order.ticker }}
                  </a>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-nowrap">{{ setupText(row.order) }}</td>
                <td class="text-nowrap">
                  <span class="badge" :class="roleBadge(row.order.bracket_role)">{{ roleText(row.order.bracket_role) }}</span>
                  <span v-if="row.orphan" class="text-muted ms-1" title="Its entry is not on this page">·</span>
                </td>
                <td class="text-nowrap">{{ row.order.order_type || '—' }}<span v-if="row.order.time_in_force" class="text-muted ms-1">{{ row.order.time_in_force }}</span></td>
                <td class="text-end">{{ num(row.order.qty, 0) }}</td>
                <td class="text-end text-nowrap">{{ priceText(row.order) }}</td>
                <td class="text-end">{{ num(row.order.filled_qty, 0) }}</td>
                <td class="text-end">{{ num(row.order.avg_fill_price, 2) }}</td>
                <td class="text-nowrap">
                  <span class="badge" :class="stateBadge(row.order.state)"
                        :title="row.order.reject_reason || null">{{ row.order.state || '—' }}</span>
                </td>
                <td class="text-nowrap">
                  <span v-if="END_REASONS[row.order.end_reason]" class="badge" :class="END_REASONS[row.order.end_reason].badge"
                        :title="END_REASONS[row.order.end_reason].title">{{ END_REASONS[row.order.end_reason].label }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="total > limit" class="card-footer d-flex justify-content-end gap-2">
        <button class="btn btn-sm btn-outline-secondary" :disabled="offset === 0" @click="page(-1)">Newer</button>
        <button class="btn btn-sm btn-outline-secondary" :disabled="offset + limit >= total" @click="page(1)">Older</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Orders: the durable broker_orders table (GET /api/orders/history).
 *
 * The live book is /api/orders/resting/; this page is the record of how each
 * order ended. A bracket is drawn as its entry with its stop and target legs
 * indented under it (utils/orderGroups.js), and "Entries that never filled"
 * is one click: entries that ended at the 15:00 cut-off or expired.
 * Filters live in the query string, so a view can be linked.
 */
import { NAV_ITEMS } from '../constants/navItems';
import { fetchOrderHistory } from '../services/api';
import { END_REASONS, NEVER_FILLED, etTime, groupOrders, priceText } from '../utils/orderGroups';

const STATES = ['NEW', 'HELD', 'ACCEPTED', 'PARTIALLY_FILLED', 'FILLED', 'CANCELED', 'REJECTED', 'EXPIRED'];
const FILTER_KEYS = ['session_date', 'ticker', 'bracket_role', 'state', 'end_reason'];
const PAGE_SIZE = 200;

function filtersFrom(query) {
  const out = {};
  for (const k of FILTER_KEYS) out[k] = String(query[k] || '');
  return out;
}

export default {
  name: 'OrderHistory',
  data() {
    return {
      NAV_ITEMS, END_REASONS, NEVER_FILLED, STATES,
      filters: filtersFrom(this.$route.query),
      offset: Number(this.$route.query.offset) || 0,
      limit: PAGE_SIZE,
      orders: [],
      legs: [],
      total: 0,
      loading: false,
      error: null,
    };
  },
  computed: {
    rows() {
      return groupOrders(this.orders, this.legs);
    },
    isNeverFilledReasons() {
      return this.filters.end_reason === NEVER_FILLED.end_reason;
    },
    neverFilledActive() {
      return this.filters.bracket_role === NEVER_FILLED.bracket_role && this.isNeverFilledReasons;
    },
  },
  watch: {
    '$route.query'(query) {
      this.filters = filtersFrom(query);
      this.offset = Number(query.offset) || 0;
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    etTime,
    priceText,
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const params = { ...this.filters, ticker: this.filters.ticker.trim().toUpperCase(),
                         limit: this.limit, offset: this.offset };
        const data = await fetchOrderHistory(params);
        this.orders = data.orders || [];
        this.legs = data.legs || [];
        this.total = data.total || 0;
      } catch (e) {
        const detail = e && e.response && e.response.data && e.response.data.detail;
        this.error = `Could not load orders${detail ? `: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}` : ''}`;
        this.orders = [];
        this.legs = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    push(filters, offset = 0) {
      const query = {};
      for (const k of FILTER_KEYS) if (filters[k]) query[k] = k === 'ticker' ? filters[k].trim().toUpperCase() : filters[k];
      if (offset) query.offset = String(offset);
      this.$router.push({ name: 'OrderHistory', query }).catch(() => {});
    },
    apply() {
      this.push(this.filters);
    },
    clear() {
      this.push({});
    },
    toggleNeverFilled() {
      const next = { ...this.filters };
      if (this.neverFilledActive) {
        next.bracket_role = '';
        next.end_reason = '';
      } else {
        Object.assign(next, NEVER_FILLED);
      }
      this.push(next);
    },
    page(direction) {
      this.push(this.filters, Math.max(0, this.offset + direction * this.limit));
    },
    postMortemHref(o) {
      const query = { ticker: o.ticker };
      if (o.session_date) query.date = o.session_date;
      if (o.e2etransactionid) query.e2etransactionid = o.e2etransactionid;
      return this.$router.resolve({ name: 'PostMortem', query }).href;
    },
    isEntry(row) {
      return !row.depth && row.order.bracket_role === 'entry';
    },
    setupText(o) {
      return o.setup ? o.setup.replace(/^camarilla_/, '').replace(/_/g, ' ').toUpperCase() : '—';
    },
    roleText(role) {
      return { entry: 'entry', stop_leg: 'stop', target_leg: 'target' }[role] || 'order';
    },
    roleBadge(role) {
      return { entry: 'bg-primary', stop_leg: 'bg-danger-subtle text-danger-emphasis border',
               target_leg: 'bg-success-subtle text-success-emphasis border' }[role] || 'bg-light text-dark border';
    },
    stateBadge(state) {
      return { FILLED: 'bg-success', PARTIALLY_FILLED: 'bg-info text-dark', CANCELED: 'bg-secondary',
               EXPIRED: 'bg-warning text-dark', REJECTED: 'bg-danger', HELD: 'bg-light text-dark border' }[state]
        || 'bg-light text-dark border';
    },
    num(v, digits) {
      return v === null || v === undefined ? '—' : Number(v).toFixed(digits);
    },
  },
};
</script>

<style scoped>
.entry-row td {
  border-top: 2px solid var(--bs-border-color);
}
.leg-row td {
  background-color: var(--bs-tertiary-bg, #f8f9fa);
}
.leg-row td:nth-child(3) {
  padding-left: 1.75rem;
}
</style>
